import React, { useState, useEffect } from "react";
import OfferStep from "./OfferStep";
import PrimaryButton from "../PrimaryButton";
import { store } from "../../../redux/store";
import { OfferFormProvider, useOfferFormContext } from "./OfferFormContext";

// Version: 1.1 - Added Store Locations and connected lines to borders
const OfferFormLayoutContent = ({
  content,
  stepIndex,
  handleSaveAndExit,
  onStepClick,
  handlePrevious,
  showPrevious = true,
  showNext = true,
  overrideNextLabel,
  currentOfferType = null,
  allowDirectStepNavigation = false,
}) => {
  const {
    nextLabel,
    isNextDisabled,
    isNextLoading,
    triggerNext
  } = useOfferFormContext();

  const [role, setRole] = useState(null);
  const [offerType, setOfferType] = useState(null);
  const [offerId, setOfferId] = useState(null);

  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        setRole(state?.authReducer?.tokenData?.user?.role || null);
        setOfferType(state?.offerReducer?.createdOffer?.offerType || null);
        setOfferId(state?.offerReducer?.createdOffer?._id || null);
      } catch (error) {
        console.warn('OfferFormLayout store access error:', error.message);
      }
    };

    updateState();
    const unsubscribe = store.subscribe(updateState);
    return () => unsubscribe();
  }, []);

  const baseSteps = [
    { stepNumber: 1, content: "Category" },
    { stepNumber: 2, content: "Subcategory" },
    { stepNumber: 3, content: "About Offer" },
    { stepNumber: 4, content: "Customisation" },
    { stepNumber: 5, content: "Rules & Regulations" },
  ];

  const adminBaseSteps = [
    { stepNumber: 1, content: "Merchant" },
    { stepNumber: 2, content: "Category" },
    { stepNumber: 3, content: "Subcategory" },
    { stepNumber: 4, content: "About Offer" },
    { stepNumber: 5, content: "Customisation" },
    { stepNumber: 6, content: "Rules & Regulations" },
  ];

  const currentBaseSteps = role === "admin" ? adminBaseSteps : baseSteps;

  // Conditionally include "Store Locations"
  const formSteps = [...currentBaseSteps];
  const aboutOfferStep = role === "admin" ? 4 : 3;

  // Show locations if:
  // 1. It's not an Online Offer
  // 2. OR it's a brand new offer (no ID yet) and we haven't passed the About Offer step
  const effectiveOfferType = currentOfferType || offerType;
  const showLocations =
    effectiveOfferType !== "ONLINE_OFFER" ||
    (stepIndex <= aboutOfferStep && !offerId);

  if (showLocations) {
    formSteps.push({ stepNumber: formSteps.length + 1, content: "Store Locations" });
  }
  formSteps.push({ stepNumber: formSteps.length + 1, content: "Review & Submit" });

  const nextButtonContent = isNextLoading ? (
    <span className="flex items-center justify-center gap-1.5">
      <span className="offer-btn-spinner" />
      <span className="text-[13px]">Processing...</span>
    </span>
  ) : (
    overrideNextLabel || nextLabel
  );

  return (
    <div className="col-span-12 mainFormDiv flex flex-col !pb-0">
      {/* Content Area - No extra padding at bottom to keep container height stable */}
      <div className="px-4 md:px-7 w-[100%] pt-4 md:pt-8 pb-[160px] flex flex-col">
        <div key={stepIndex} className="step-transition">
          {content}
        </div>
      </div>

      {/* Floating Bottom Section - Stepper and Navigation */}
      <div className="sticky bottom-0 left-0 w-full z-[100] pb-0">
        <div className="w-full bg-white shadow-[0px_-4px_20px_0px_#0000001a] border-t border-[#f0f0f0] overflow-hidden">
          {/* Stepper Row */}
          <div className="w-full pt-4 md:pt-6 px-0">
            {/* Desktop View: Detailed Stepper */}
            <div className="hidden md:flex items-center w-full px-0">
              <div className="flex-1 flex items-center overflow-x-auto no-scrollbar">
                {formSteps?.map((step, index) => (
                  <OfferStep
                    key={index}
                    stepNumber={step?.stepNumber}
                    content={step?.content}
                    stepIndex={stepIndex}
                    isClickable={
                      allowDirectStepNavigation
                        ? step.stepNumber !== stepIndex
                        : step.stepNumber < stepIndex
                    }
                    onClick={onStepClick}
                    isLast={index === formSteps.length - 1}
                  />
                ))}
              </div>
            </div>

            {/* Mobile View: Simplified Progress Bar */}
            <div className="flex md:hidden flex-col w-full px-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#00ADEF] text-[13px] font-[600] font-poppins">
                  Step {stepIndex}/{formSteps.length}
                </span>
                <span className="text-[#303030] text-[13px] font-[500] font-poppins truncate max-w-[60%] text-right">
                  {formSteps[stepIndex - 1]?.content}
                </span>
              </div>
              <div className="w-full h-[5px] bg-[#F2F2F2] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#00ADEF] transition-all duration-300 ease-in-out rounded-full"
                  style={{ width: `${(stepIndex / formSteps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between w-full px-4 md:px-8 pb-4 pt-4">
            <div className="ml-2">
              {showPrevious && stepIndex > 1 && (
                <span
                  onClick={handlePrevious}
                  className="text-[#00ADEF] font-poppins text-[14px] md:text-[16px] font-[500] cursor-pointer hover:opacity-80 border-b border-[#00ADEF]"
                >
                  Back
                </span>
              )}
            </div>

            <div className="mr-2">
              {showNext && (
                <PrimaryButton
                  className="min-w-[100px] md:min-w-[120px] w-auto px-6 h-[40px] md:h-[45px] rounded-[5px] text-[14px] md:text-[16px] font-[500] font-montserrat"
                  label={nextButtonContent}
                  disabled={isNextDisabled || isNextLoading}
                  onClick={triggerNext}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OfferFormLayout = (props) => {
  return (
    <OfferFormProvider>
      <OfferFormLayoutContent {...props} />
    </OfferFormProvider>
  );
};

export default OfferFormLayout;
